import { sleep } from './helpers';

export interface PollingConfig<T> {
	checkStatus: () => Promise<T>;
	shouldContinue: (data: T) => boolean;
	isFailed?: (data: T) => boolean;
	getStatusMessage?: (data: T) => string;
	interval: number;
	timeout?: number;
	retries?: number;
	onStatusChange?: (message: string) => void;
	defaultStatusMessage?: string;
}

export interface PollingResult<T> {
	success: boolean;
	data: T | null;
	error?: string;
}

export async function pollWithTimeout<T>(config: PollingConfig<T>): Promise<PollingResult<T>> {
	const {
		checkStatus,
		shouldContinue,
		isFailed = () => false,
		getStatusMessage,
		interval,
		timeout,
		retries = 0,
		onStatusChange,
		defaultStatusMessage = 'Processing...',
	} = config;

	const abortController = new AbortController();
	let timeoutId: NodeJS.Timeout | undefined;

	if (timeout && timeout > 0) {
		timeoutId = setTimeout(() => abortController.abort(), timeout);
	}

	async function poll(): Promise<PollingResult<T>> {
		try {
			if (abortController.signal.aborted) {
				throw new Error('Operation aborted');
			}

			const data = await checkStatus();
			const statusMessage = getStatusMessage?.(data) || defaultStatusMessage;

			if (onStatusChange && statusMessage) {
				onStatusChange(statusMessage);
			}

			if (isFailed?.(data)) {
				if (retries > 0) {
					await sleep(interval);
					return poll();
				}
				return { success: false, data: null, error: 'Operation failed' };
			}

			if (!shouldContinue(data)) {
				return { success: true, data };
			}

			await sleep(interval);
			return poll();
		} catch (error) {
			if (error instanceof Error && error.message === 'Operation aborted') {
				throw error;
			}
			await sleep(interval);
			return poll();
		}
	}

	try {
		if (timeout && timeout > 0) {
			return await Promise.race([
				poll(),
				new Promise<PollingResult<T>>((_, reject) => {
					abortController.signal.addEventListener('abort', () => {
						reject(new Error('Operation timed out'));
					});
				}),
			]);
		}
		return await poll();
	} catch (error) {
		return {
			success: false,
			data: null,
			error: error instanceof Error ? error.message : 'Unknown error occurred',
		};
	} finally {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	}
}
